import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { postJson } from "@/hooks/use-shares";
import { getJson } from "@/lib/api-client";
import { checklistKeys, projectKeys } from "@/lib/query";
import { type AssignPhotosRequest, checklistResponseSchema } from "@/schemas/checklist";

export function useChecklist(projectId: string, enabled = true) {
  return useQuery({
    queryKey: checklistKeys.project(projectId),
    queryFn: async () =>
      (
        await getJson(
          `/api/projects/${encodeURIComponent(projectId)}/checklist`,
          checklistResponseSchema,
        )
      ).data,
    enabled,
  });
}

function useChecklistMutation<Request>(url: (itemId: string) => string, failure: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, request }: { itemId: string; request: Request }) =>
      postJson(url(itemId), request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: checklistKeys.all });
      // The header and the home grid show progress too.
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : failure);
    },
  });
}

export function useAssignPhotos() {
  return useChecklistMutation<AssignPhotosRequest>(
    (itemId) => `/api/checklist-items/${encodeURIComponent(itemId)}/photos`,
    "Could not change the photos on this item",
  );
}

export function useSetCheck() {
  return useChecklistMutation<{ checked: boolean }>(
    (itemId) => `/api/checklist-items/${encodeURIComponent(itemId)}/check`,
    "Could not change this item",
  );
}
